// Local article data-access layer, backed entirely by the committed snapshot in
// src/data/articles/ (generated by `npm run fetch-articles`). The app reads ONLY
// this snapshot at runtime — it never calls the SEObot API. To refresh content,
// re-run the fetch script and commit the changed data files.
//
// Bundle strategy:
//   - The lightweight index (index.json, summaries only) is statically imported
//     and used by the /articles grid.
//   - Full article bodies are code-split per slug via a non-eager glob, so the
//     client only downloads the body of the article actually being viewed. The
//     full bodies are NEVER all bundled into the main client chunk.
//   - For a prerendered /post/:slug page, the build inlines that article's full
//     data into the HTML as `window.__ARTICLE__`. The client reads it
//     synchronously on first render, so hydration matches the server markup with
//     no flash and no extra request. Client-side navigation then lazy-loads other
//     articles' bodies via the per-slug glob.

import type { Article, ArticleListResponse } from '../types/article';
import indexData from '../data/articles/index.json';

// Per-slug full-article modules, code-split (NOT eager). Each value is a loader
// function returning a dynamic import. Used for client-side navigation and by the
// prerender (which awaits the loader before rendering).
const postLoaders = import.meta.glob<{ default: Article }>(
  '../data/articles/posts/*.json'
);

// slug -> loader, derived from the glob keys (".../posts/<slug>.json").
const loaderBySlug: Record<string, () => Promise<{ default: Article }>> = {};
for (const filePath in postLoaders) {
  const slug = filePath.split('/').pop()!.replace(/\.json$/, '');
  loaderBySlug[slug] = postLoaders[filePath];
}

// In-memory cache of bodies that have been loaded (or inlined) this session.
const loadedBySlug: Record<string, Article> = {};

// Pick up an article body inlined into the page by the prerender, if present.
// This makes the first render of a prerendered /post/:slug page synchronous.
const readInlinedArticle = (): Article | null => {
  if (typeof window === 'undefined') return null;
  const inlined = (window as unknown as { __ARTICLE__?: Article }).__ARTICLE__;
  if (inlined && inlined.slug) {
    loadedBySlug[inlined.slug] = inlined;
    return inlined;
  }
  return null;
};

// Lightweight summaries (no `content`) used by the grid, in snapshot order
// (newest first). Typed as Article — the body is just an empty string here.
const articleIndex = indexData as unknown as Article[];

// Normalize a category label or slug into a comparable token, so the existing
// category-filter slugs ("leadership", "career-growth", ...) match snapshot
// category titles ("Leadership", "Career Development", ...) where they line up.
const normalizeCategory = (value: string): string =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

export interface GetArticlesParams {
  page?: number;
  pageSize?: number;
  category?: string;
  tag?: string;
  search?: string;
}

// Return a paginated, optionally filtered slice of the article index.
// Mirrors the shape the old seobotClient.getArticles returned.
export const getArticles = (params: GetArticlesParams = {}): ArticleListResponse => {
  const page = params.page ?? 0;
  const pageSize = params.pageSize ?? 12;

  let articles = articleIndex;

  if (params.category && params.category !== 'all') {
    const target = normalizeCategory(params.category);
    articles = articles.filter((a) => normalizeCategory(a.category) === target);
  }

  if (params.tag) {
    const target = params.tag.toLowerCase();
    articles = articles.filter((a) =>
      (a.tags || []).some((t) => t.toLowerCase() === target)
    );
  }

  if (params.search) {
    const q = params.search.toLowerCase();
    articles = articles.filter(
      (a) =>
        a.title.toLowerCase().includes(q) ||
        a.description.toLowerCase().includes(q)
    );
  }

  const total = articles.length;
  const start = page * pageSize;
  const pageItems = articles.slice(start, start + pageSize);

  return {
    articles: pageItems,
    total,
    page,
    pageSize,
    hasMore: start + pageSize < total,
  };
};

// Synchronously return a full article body if it is already available — i.e. it
// was inlined by the prerender for this page, or previously loaded this session.
// Returns null if the body still needs to be fetched (use loadArticle).
export const getArticleSync = (slug: string): Article | null => {
  if (loadedBySlug[slug]) return loadedBySlug[slug];
  const inlined = readInlinedArticle();
  if (inlined && inlined.slug === slug) return inlined;
  return null;
};

// Asynchronously load a full article body (with `content`) by slug, code-split so
// only the requested article is downloaded. Returns null if the slug is not in
// the snapshot. Used for client-side navigation between articles.
export const loadArticle = async (slug: string): Promise<Article | null> => {
  const cached = getArticleSync(slug);
  if (cached) return cached;
  const loader = loaderBySlug[slug];
  if (!loader) return null;
  const mod = await loader();
  loadedBySlug[slug] = mod.default;
  return mod.default;
};

// Whether a slug exists in the snapshot (without loading its body).
export const hasArticle = (slug: string): boolean => Boolean(loaderBySlug[slug]);

// Every article slug in the snapshot. Used by the prerender script to enumerate
// the /post/:slug routes to statically generate.
export const getAllSlugs = (): string[] => Object.keys(loaderBySlug);

// Derive related articles from the snapshot: prefer same-category articles, then
// backfill with the most recent others. Excludes the current article. Replaces
// the old hard-coded mock related list. Operates on the index (summaries only),
// which is all the related-article cards need.
export const getRelatedArticles = (article: Article, limit = 3): Article[] => {
  const sameCategory = articleIndex.filter(
    (a) => a.slug !== article.slug && a.category === article.category
  );
  const others = articleIndex.filter(
    (a) => a.slug !== article.slug && a.category !== article.category
  );
  return [...sameCategory, ...others].slice(0, limit);
};
